/**
 * Editorial content served by the detail endpoint under `editorialContent`.
 *
 * The backend resolves the spirit's editorial profile and strips every field, axis, tag and
 * affinity that does not apply to it, so the frontend renders whatever arrives and never
 * decides that, say, a vodka should hide its maturation block.
 *
 * Two properties of the contract shape this model:
 *
 *  - it is ADDITIVE and every block is optional. A response may carry prose without a sensory
 *    profile, a sensory profile without prose, or any combination in between, so each block is
 *    read on its own and its absence falls back to the legacy catalogue data on its own;
 *  - an absent block is not an empty block. The backend omits what it cannot publish instead
 *    of sending a placeholder, which is what lets the sheet close a gap rather than draw one.
 */

export interface EditorialNarrative {
    narrative?: string | null;
}

export interface EditorialTasting {
    appearance?: string | null;
    nose?:       string | null;
    palate?:     string | null;
    finish?:     string | null;
}

/**
 * The twelve production fields of `ContentSection::PRODUCTION`. A profile only ever receives
 * the subset its editorial spec allows.
 */
export interface EditorialProduction {
    rawMaterial?:       string | null;
    fermentation?:      string | null;
    distillation?:      string | null;
    maturation?:        string | null;
    finishCask?:        string | null;
    botanicals?:        string | null;
    aromatisation?:     string | null;
    filtration?:        string | null;
    water?:             string | null;
    distillery?:        string | null;
    origin?:            string | null;
    ageClassification?: string | null;
}

export interface EditorialIngredients {
    ingredients?: string | null;
}

/**
 * Legacy v2 fields retained in the shared API while installed clients transition. The v3 UI
 * deliberately does not render them: serving belongs to a venue and sensory values now arrive
 * through the versioned assessment block.
 */
export interface EditorialService {
    howToDrink?: string | null;
    glassware?: string | null;
    servingNote?: string | null;
}

export type EditorialProfile = Partial<Record<'body' | 'richness' | 'smoke' | 'sweetness', number>>;

/**
 * One published sensory axis.
 *
 * `value` is an integer 0-5 where 0 asserts a real absence. An axis nobody could establish is
 * NOT sent as 0: the backend omits the row, so an axis that is missing from this list is a
 * "we do not know" and must not be drawn.
 */
export interface SensoryAxis {
    axis:  string;
    /** Canonical label of the methodology; used only when the locale has no key for the axis. */
    label: string;
    value: number;
    max:   number;
}

/** A controlled-vocabulary character descriptor. `iconKey` is the visual GROUP, not the term. */
export interface CharacterTag {
    code:    string;
    label:   string;
    iconKey: string;
}

/** A gastronomic family, never a dish, a service or an availability of the venue. */
export interface Affinity {
    code:    string;
    label:   string;
    iconKey: string;
}

export type FactValue = 'true' | 'false' | 'unknown';

export interface ProductFact {
    key:           string;
    value:         FactValue;
    /** Date the evidence carries, not the date it was stored. Absent on investigated unknowns. */
    evidenceDate?: string | null;
}

/**
 * A verified award. The external score travels with its own scale and is never mixed with our
 * sensory axes, so `98/100` stays a third-party statement instead of becoming a rating of ours.
 */
export interface Award {
    competition:    string;
    editionYear?:   number | null;
    /** Kept literally: "Double Gold" is not "Gold". */
    result?:        string | null;
    externalScore?: number | null;
    scoreScale?:    string | null;
}

export interface EditorialContent {
    /** Locale the content was served in; no cross-locale fallback happens upstream. */
    locale:           string;
    family:           string;
    editorialProfile?: string;
    narrative?:       EditorialNarrative;
    tasting?:         EditorialTasting;
    production?:      EditorialProduction;
    ingredients?:     EditorialIngredients;
    /** @deprecated Legacy API compatibility only; v3 does not render venue serving advice. */
    service?:         EditorialService;
    /** @deprecated Legacy API compatibility only; v3 uses `sensory`. */
    profile?:         EditorialProfile;
    sensory?:         SensoryAxis[];
    characterTags?:   CharacterTag[];
    affinities?:      Affinity[];
    productFacts?:    ProductFact[];
    awards?:          Award[];
}

/** One rendered tab: an i18n label key plus the text the backend sent. */
export interface EditorialTab {
    field: string;
    label: string;
    text:  string;
}

/**
 * Display maxima given by product direction. The backend already applies them after merging
 * human and AI rows; they are repeated here so a contract change upstream cannot silently turn
 * the sheet into a list.
 */
export const MAX_CHARACTER_TAGS = 5;
export const MAX_AFFINITIES = 3;

/** True when the string carries something worth rendering. */
export function hasText(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim().length > 0;
}

function anyText(section: object | undefined): boolean {
    return !!section && Object.values(section).some(hasText);
}

/**
 * Per-block presence. These are the ONLY switches the sheet uses to decide between editorial
 * content and legacy catalogue data: a global "is there any editorial content" flag would make
 * an assessment-only spirit lose the legacy prose it already had.
 */
export function hasNarrativeBlock(content: EditorialContent | null | undefined): boolean {
    return hasText(content?.narrative?.narrative);
}

export function hasTastingBlock(content: EditorialContent | null | undefined): boolean {
    return anyText(content?.tasting);
}

export function hasProductionBlock(content: EditorialContent | null | undefined): boolean {
    return anyText(content?.production);
}

export function hasIngredientsBlock(content: EditorialContent | null | undefined): boolean {
    return hasText(content?.ingredients?.ingredients);
}

export function hasSensoryBlock(content: EditorialContent | null | undefined): boolean {
    return publishedAxes(content).length > 0;
}

export function hasCharacterBlock(content: EditorialContent | null | undefined): boolean {
    return characterTags(content).length > 0;
}

export function hasAffinitiesBlock(content: EditorialContent | null | undefined): boolean {
    return affinities(content).length > 0;
}

/**
 * Axes that can be drawn. A row without an integer value is dropped rather than rendered as 0,
 * which would assert an absence the backend never claimed.
 */
export function publishedAxes(content: EditorialContent | null | undefined): SensoryAxis[] {
    return (content?.sensory ?? []).filter(axis =>
        hasText(axis?.axis)
        && hasText(axis?.label)
        && typeof axis?.value === 'number'
        && Number.isFinite(axis.value)
        && Number.isInteger(axis.value)
        && typeof axis.max === 'number'
        && Number.isFinite(axis.max)
        && Number.isInteger(axis.max)
        && axis.max > 0
        && axis.value >= 0
        && axis.value <= axis.max
    );
}

export function characterTags(content: EditorialContent | null | undefined): CharacterTag[] {
    return (content?.characterTags ?? [])
        .filter(tag => hasText(tag?.label))
        .slice(0, MAX_CHARACTER_TAGS);
}

export function affinities(content: EditorialContent | null | undefined): Affinity[] {
    return (content?.affinities ?? [])
        .filter(affinity => hasText(affinity?.label))
        .slice(0, MAX_AFFINITIES);
}

export function productFacts(content: EditorialContent | null | undefined): ProductFact[] {
    return (content?.productFacts ?? []).filter(fact =>
        hasText(fact?.key) && ['true', 'false', 'unknown'].includes(fact?.value)
    );
}

/** True when an award has either a literal result or a meaningful, scaled external score. */
export function isRenderableAward(award: Award | null | undefined): award is Award {
    return !!award
        && hasText(award.competition)
        && (
            hasText(award.result)
            || (
                typeof award.externalScore === 'number'
                && Number.isFinite(award.externalScore)
                && hasText(award.scoreScale)
            )
        );
}

export function awards(content: EditorialContent | null | undefined): Award[] {
    return (content?.awards ?? []).filter(isRenderableAward);
}
